import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './_styles/icon.scss?inline'

export type IconSize = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'

/**
 * `dap-ds-icon`
 * @summary An icon is a graphical symbol that represents an object or action.
 * @element dap-ds-icon
 * @title - Icon
 *
 * @property {boolean} ariaHidden - Whether the icon is hidden from the accessibility tree. Default is true.
 * @property {string} ariaLabel - The aria label of the icon.
 *
 * @slot - The content of the icon for custom svg icons.
 *
 * @csspart base - The main icon container.
 * @csspart icon - The icon of the icon.
 *
 * @cssproperty --dds-icon-size-xs - The size of extra small icons (default: 12px)
 * @cssproperty --dds-icon-size-sm - The size of small icons (default: 16px)
 * @cssproperty --dds-icon-size-md - The size of medium icons (default: 20px)
 * @cssproperty --dds-icon-size-lg - The size of large icons (default: 24px)
 * @cssproperty --dds-icon-size-xl - The size of extra large icons (default: 32px)
 * @cssproperty --dds-icon-size-xxl - The size of extra extra large icons (default: 40px)
 */
export default class DapDSIcon extends DdsElement {
  static tagName = 'dap-ds-icon'

  /** The name of the icon */
  @property() name: string
  /** The size of the icon
   * @type {'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'}
   */
  @property() size: 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' = 'md'
  /** The size of the icon in pixels. This overrides the size attribute. */
  @property({ type: Number }) staticSize?: number
  /** Whether the icon is focusable. Default is false. */
  @property({ type: Boolean }) focusable?: boolean = false

  static readonly styles = unsafeCSS(styles)

  /** True unless the consumer explicitly opted out with `aria-hidden="false"`. */
  private get _isDecorative(): boolean {
    return this.ariaHidden !== 'false'
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.willUpdate(_changedProperties)

    // Icons are decorative by default, so the host is hidden from the a11y tree.
    // Consumers opt out with aria-hidden="false" (kept as an explicit "false"
    // rather than removing the attribute, which is how the opt-out is tracked).
    this.setAttribute('aria-hidden', this._isDecorative ? 'true' : 'false')
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    this.classList.add('dap-ds-icon')
  }

  render() {
    return html`
      <span
        class=${clsx('icon', {})}
        part="base"
        tabindex="-1"
        aria-hidden=${this._isDecorative ? 'true' : 'false'}>
        ${this.name ? this._getNamedIcon() : this._getSlotIcon()}
      </span>
    `
  }

  private _getNamedIcon() {
    const IconTag = `dap-ds-icon-${this.name}`

    return html`
     <${unsafeStatic(IconTag)}
        part="icon"
        size=${this.staticSize ?? this._getIconSizePx(this.size)}
        viewBox="0 0 24 24"
        role="img"
        aria-hidden=${this._isDecorative ? 'true' : 'false'}
        aria-label=${this.ariaLabel ?? ''}
        tabindex=${this.focusable ? 0 : -1}
      >
      </${unsafeStatic(IconTag)}
    `
  }

  private _getSlotIcon() {
    return html`
      <span
        part="icon"
        class=${clsx('icon--custom-icon', { [this.size]: true })}
        aria-hidden=${this._isDecorative ? 'true' : 'false'}>
        <slot></slot>
      </span>
    `
  }

  private _getIconSizePx(size: IconSize): number {
    const defaultSize = 20
    switch (size) {
      case 'xxl':
        return 40
      case 'xl':
        return 32
      case 'lg':
        return 24
      case 'md':
        return defaultSize
      case 'sm':
        return 16
      case 'xs':
        return 12
      default:
        return defaultSize
    }
  }
}
